import { createContext, useContext, useState, ReactNode } from 'react';
import { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

export interface Notification {
  id: string;
  type: 'import' | 'reassign' | 'error' | 'info' | 'warning' | 'success' | 'rollover' | 'timer';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: any;
  targetRoles?: AppRole[]; // Which roles can see this notification
  targetTechId?: string; // Specific technician who should see this (for TECHNICIAN role)
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'> | Notification) => void;
  clearAll: () => void;
  markAsRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  filterNotificationsByRole: (role: AppRole, techId?: string) => Notification[];
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/** W4b: the server-authorized end-of-day decision kinds (see the notifications SELECT policy, 20270206061000). */
export const ROLLOVER_DECISION_NOTIFICATION_KINDS = ['rollover_decision_needed', 'rollover_scope_exceeded', 'rollover_needs_placement'] as const;
export function isRolloverDecisionNotification(n: Pick<Notification, 'data'>): boolean {
  const kind = (n.data as { kind?: unknown } | null | undefined)?.kind;
  return typeof kind === 'string' && (ROLLOVER_DECISION_NOTIFICATION_KINDS as readonly string[]).includes(kind);
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'> | Notification) => {
    // If notification already has an id, timestamp, and read status, use it as-is (from DB)
    if ('id' in notification && 'timestamp' in notification && 'read' in notification) {
      // Check if notification with this ID already exists
      setNotifications(prev => {
        const exists = prev.some(n => n.id === notification.id);
        if (exists) {
          return prev; // Don't add duplicates
        }
        return [notification as Notification, ...prev];
      });
    } else {
      // Generate new notification (for in-memory notifications)
      const newNotification: Notification = {
        ...notification,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        read: false,
      };
      setNotifications(prev => [newNotification, ...prev]);
    }
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const filterNotificationsByRole = (role: AppRole, techId?: string): Notification[] => {
    return notifications.filter(notification => {
      // If no target roles specified, show to everyone
      if (!notification.targetRoles) return true;

      // W4b: end-of-day decision notifications follow the server's decision predicate, not the scalar role. The
      // notifications SELECT policy (20270206061000) returns these rows only to canonical approvers
      // (is_manager_or_writer — e.g. a multi-role service advisor whose scalar role is TECHNICIAN), so a row that
      // reached this list is already authorized; filtering it again by the scalar role would hide it from them.
      if (isRolloverDecisionNotification(notification)) return true;
      
      // MANAGER and SERVICE_WRITER see everything
      if (role === 'MANAGER' || role === 'SERVICE_WRITER') return true;
      
      // TECHNICIAN only sees notifications targeted to them or their tickets
      if (role === 'TECHNICIAN') {
        // If notification has a specific tech target, only show to that tech
        if (notification.targetTechId) {
          return notification.targetTechId === techId;
        }
        // Otherwise, show if TECHNICIAN role is in targetRoles
        return notification.targetRoles.includes('TECHNICIAN');
      }
      
      // For other roles, check if they're in targetRoles
      return notification.targetRoles.includes(role);
    });
  };

  return (
    <NotificationContext.Provider value={{ notifications, addNotification, clearAll, markAsRead, deleteNotification, filterNotificationsByRole }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
